import { t, type SupportedLocale } from "@/lib/i18n";
import { ProductCard } from "@/components/ProductCard";
import {
  getHomeLatestProducts,
  HOME_LATEST_PRODUCTS_LIMIT,
} from "@/data/products";
import { getAllProductsUrl } from "@/lib/urls";
import styles from "@/styles/LatestProducts.module.css";

type LatestProductsSectionProps = {
  locale: SupportedLocale;
  /** Visual density: homepage uses full section padding; 404 uses compact. */
  variant?: "default" | "compact";
  limit?: number;
  titleId?: string;
};

export function LatestProductsSection({
  locale,
  variant = "default",
  limit = HOME_LATEST_PRODUCTS_LIMIT,
  titleId = "latest-products-title",
}: LatestProductsSectionProps) {
  const twelvego = getHomeLatestProducts(limit, "12GO");
  const brixago = getHomeLatestProducts(limit, "Brixago");
  if (twelvego.length === 0 && brixago.length === 0) return null;

  const seeAll = t(locale, "home.latestSeeAll");
  const seeAllBrixago = t(locale, "home.brixagoSeeAll");
  const sectionClass =
    variant === "compact" ? styles.sectionCompact : styles.section;

  return (
    <>
      {twelvego.length > 0 ? (
        <section className={sectionClass} aria-labelledby={titleId}>
          <div className={styles.inner}>
            <div className={styles.header}>
              <h2 id={titleId} className={styles.title}>
                {t(locale, "home.bestsellersSubtitle")}
              </h2>
            </div>
            <div className={styles.grid}>
              {twelvego.map((p) => (
                <ProductCard key={p.id} product={p} locale={locale} />
              ))}
            </div>
            <div className={styles.cta}>
              <a
                href={`${getAllProductsUrl(locale)}?brands=12GO`}
                className={styles.ctaLink}
                title={seeAll}
              >
                {seeAll}
              </a>
            </div>
          </div>
        </section>
      ) : null}

      {brixago.length > 0 ? (
        <section
          className={sectionClass}
          aria-labelledby={`${titleId}-brixago`}
        >
          <div className={styles.inner}>
            <div className={styles.header}>
              <h2 id={`${titleId}-brixago`} className={styles.title}>
                {t(locale, "home.brixagoTitle")}
              </h2>
            </div>
            <div className={styles.grid}>
              {brixago.map((p) => (
                <ProductCard key={p.id} product={p} locale={locale} />
              ))}
            </div>
            <div className={styles.cta}>
              <a
                href={`${getAllProductsUrl(locale)}?brands=Brixago`}
                className={styles.ctaLink}
                title={seeAllBrixago}
              >
                {seeAllBrixago}
              </a>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
